/**
 * Аудиоформаты, которые принимает бэк, — зеркало `config/allowedExtensions.php`.
 *
 * По этому же списку `FileResource` подменяет имя файла на «Аудио NNN», поэтому
 * расходиться с бэком нельзя: иначе фронт сочтёт обычным файлом то, что сервер
 * уже считает голосовым.
 *
 * `mp4` в списке бэка есть, а здесь его нет намеренно: по расширению не отличить
 * аудиодорожку от видео, а `mp4` у нас уже занят видео (см. `isVideo` в
 * `fileThumbnail`). Записи с диктофона приходят как `m4a`, так что потери нет.
 */
export const AUDIO_EXTENSIONS = [
    'wav',
    'webm',
    'ogg',
    'oga',
    'm4a',
    'mp3',
    'opus',
    'aac',
    'flac',
] as const;

/**
 * Локальная метка ещё не загруженной записи: файл существует только как blob
 * в стейте формы, расширения у него нет.
 */
export const LOCAL_AUDIO_EXTENSION = 'audio';

/** MIME контейнера → расширение файла. Ключи — то, что реально отдаёт `MediaRecorder`. */
const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
    'audio/webm': 'webm',
    'audio/ogg': 'ogg',
    'audio/mp4': 'm4a',
    'audio/mpeg': 'mp3',
    'audio/wav': 'wav',
    'audio/x-wav': 'wav',
    'audio/wave': 'wav',
    'audio/opus': 'opus',
    'audio/aac': 'aac',
    'audio/flac': 'flac',
};

/**
 * Голосовое ли это вложение.
 *
 * Старые записи лежат в базе с расширением `wav` (внутри webm — так их подписывал
 * фронт, пока бэк не принимал ничего, кроме wav), поэтому `wav` обязан остаться
 * в списке навсегда, даже если писать в нём мы уже не будем.
 */
export const isAudioExtension = (extension?: string): boolean => {
    if (!extension) return false;

    const normalized = extension.toLowerCase();

    return (
        normalized === LOCAL_AUDIO_EXTENSION ||
        AUDIO_EXTENSIONS.includes(normalized as (typeof AUDIO_EXTENSIONS)[number])
    );
};

/**
 * Расширение для файла записи по MIME контейнера, который выбрал браузер.
 *
 * Неизвестный контейнер отдаём как есть (`audio/3gpp` → `3gpp`): бэк такое отклонит,
 * и это правильнее, чем подписать файл чужим расширением — ровно из-за такой подписи
 * webm-записи годами лежали как `.wav`.
 */
export const getAudioExtensionByMimeType = (mimeType: string): string => {
    // MediaRecorder отдаёт тип с параметрами: `audio/webm;codecs=opus`
    const baseMimeType = mimeType.split(';')[0].trim().toLowerCase();

    return EXTENSION_BY_MIME_TYPE[baseMimeType] ?? baseMimeType.split('/').at(-1) ?? 'webm';
};
